import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import { useSubscription } from '../../hooks/useSubscription';
import getRatio from '../../lib/getRatio';
import normalizeCardTheme from '../../lib/normalizeCardTheme';

import RoundedButton from '../buttons/RoundedButton';
import ToggleButton from '../buttons/ToggleButton';
import ViaButton from '../buttons/ViaButton';
import LoadingIcon from '../icons/LoadingIcon';
import ToggleIcon from '../icons/ToggleIcon';
import AuthorImage from '../partials/AuthorImage';
import Image from '../partials/Image';
import EditContactModal from '../popups/EditContactModal';
import CardDescription from '../typography/CardDescription';
import CardTitle from '../typography/CardTitle';
import HorizontalCard from './HorizontalCard';

import styles from '../../styles/cards/subscription-horizontal-card.module.css';

interface SubscriptionHorizontalCardProps extends Partial<Subscription> {
    url?: string | null;
    thumbnailWidth?: number | null;
    height?: number | null;
    ratio?: CardRatio | null;
    theme?: CardTheme | LegacyCardTheme | null;
    source?: string | null;
    checked?: boolean;
    withButton?: boolean;
    withToggle?: boolean;
    withCheckbox?: boolean;
    onSubscribe?: (() => void) | null;
    onUnsubscribe?: (() => void) | null;
    onChange?: ((checked: boolean) => void) | null;
    className?: string | null;
}
function SubscriptionHorizontalCard({
    url = null,
    author = null,
    type,
    brand = null,
    handle,
    image = null,
    thumbnailWidth = null,
    height = 110,
    ratio = 'square',
    name = null,
    via = null,
    description = null,
    theme: providedTheme = null,
    source = null,
    checked = false,
    withButton = false,
    withToggle = false,
    withCheckbox = false,
    onSubscribe = null,
    onUnsubscribe = null,
    onChange = null,
    className = null,
}: SubscriptionHorizontalCardProps) {
    const theme = normalizeCardTheme(providedTheme);
    const thumbnailRatio = getRatio(ratio, null);
    const {
        subscribed,
        via: userVia,
        isSubscribing,
        isUnsubscribing,
        subscribe,
        unsubscribe,
        updateVia,
        editContactNeeded,
        closeEditContact,
        onEditContactComplete,
    } = useSubscription({
        subscription: handle,
        source,
        onSubscribe,
        onUnsubscribe,
    });
    const onSubscribeChange = (newSubscribed) => {
        if (newSubscribed) {
            subscribe();
        } else {
            unsubscribe();
        }
    };
    const isExternal = type === 'external';
    const { name: authorName, description: authorDescription = null } = author || {};
    const { description: brandDescription = null } = brand || {};
    const finalThumbnailWidth = thumbnailWidth || thumbnailRatio * height;
    const finalThumbnailHeight = thumbnailWidth !== null ? thumbnailWidth / thumbnailRatio : height;
    const onCheckboxChange = (e) => {
        if (onChange !== null) {
            onChange(e.target.checked);
        }
    };
    return (
        <HorizontalCard
            theme={theme || 'none'}
            height={height}
            thumbnailRatio={thumbnailRatio}
            // responsive
            isLabel={withCheckbox}
            thumbnail={
                author !== null ? (
                    <AuthorImage
                        name={authorName}
                        width={finalThumbnailWidth}
                        height={finalThumbnailHeight}
                        media={image}
                        size="small"
                        className={styles.thumbnail}
                        pictureClassName={styles.picture}
                        responsive={thumbnailWidth === null}
                    />
                ) : (
                    <Image
                        width={finalThumbnailWidth}
                        height={finalThumbnailHeight}
                        media={image}
                        size="small"
                        className={styles.thumbnail}
                        pictureClassName={styles.picture}
                        responsive={thumbnailWidth === null}
                        // withoutSize
                    />
                )
            }
            onClick={
                withToggle && !subscribed && !isExternal
                    ? () => onSubscribeChange(!subscribed)
                    : null
            }
            className={classNames([
                styles.container,
                {
                    [styles.isAuthor]: author !== null,
                    [styles.isBrand]: brand !== null,
                    [styles.subscribed]: subscribed && !withCheckbox,
                    [styles.checked]: checked,
                },
                className,
            ])}
            labelClassName={styles.label}
            thumbnailClassName={styles.thumbnail}
        >
            <div className={styles.inner}>
                <CardTitle className={styles.name}>{name}</CardTitle>
                <CardDescription
                    className={styles.description}
                    html={description || authorDescription || brandDescription}
                />
                {via !== null && via.length > 1 && subscribed && !withCheckbox ? (
                    <div className={styles.via}>
                        <strong>Recevoir par :</strong>
                        <ViaButton
                            via={via}
                            value={userVia}
                            onChange={updateVia}
                            className={styles.viaButton}
                        />
                    </div>
                ) : null}
                {withButton ? (
                    <RoundedButton
                        transparent={subscribed}
                        onClick={() => onSubscribeChange(!subscribed)}
                        disabled={isSubscribing || isUnsubscribing}
                        className={classNames([
                            styles.button,
                            {
                                [styles.unsubscribeButton]: subscribed,
                            },
                        ])}
                    >
                        {subscribed ? (
                            <FormattedMessage
                                defaultMessage="Me désabonner"
                                description="Button label"
                            />
                        ) : (
                            <FormattedMessage
                                defaultMessage="M'abonner"
                                description="Button label"
                            />
                        )}
                    </RoundedButton>
                ) : null}
            </div>
            {isExternal ? (
                <span className={styles.actions}>
                    <RoundedButton href={url} external compact>
                        S’abonner
                    </RoundedButton>
                </span>
            ) : null}
            {withToggle && !isExternal ? (
                <span className={styles.actions}>
                    {subscribed ? (
                        <ToggleButton
                            value={subscribed}
                            className={styles.toggle}
                            loading={isUnsubscribing || isSubscribing}
                            onChange={onSubscribeChange}
                        />
                    ) : (
                        <ToggleIcon
                            checked={subscribed}
                            className={styles.toggle}
                            loading={isUnsubscribing || isSubscribing}
                        />
                    )}
                </span>
            ) : null}

            {withCheckbox && !isExternal ? (
                <span className={styles.actions}>
                    <input
                        type="checkbox"
                        className={styles.checkbox}
                        checked={checked}
                        onChange={onCheckboxChange}
                    />
                </span>
            ) : null}

            {withButton ? (
                <span className={styles.actions}>
                    {isUnsubscribing || isSubscribing ? (
                        <LoadingIcon
                            loading
                            className={classNames([styles.icon, styles.loadingIcon])}
                        />
                    ) : (
                        <span
                            className={classNames([
                                'material-symbols-rounded',
                                styles.icon,
                                styles.checkIcon,
                            ])}
                        >
                            check_circle
                        </span>
                    )}
                </span>
            ) : null}
            {editContactNeeded !== null ? (
                <EditContactModal
                    type={editContactNeeded}
                    title={
                        editContactNeeded === 'phone' ? (
                            <FormattedMessage
                                defaultMessage="Cet abonnement demande un numéro de téléphone"
                                description="Modal title"
                            />
                        ) : (
                            <FormattedMessage
                                defaultMessage="Cet abonnement demande un courriel"
                                description="Modal title"
                            />
                        )
                    }
                    onComplete={onEditContactComplete}
                    onClosed={closeEditContact}
                />
            ) : null}
        </HorizontalCard>
    );
}
export default SubscriptionHorizontalCard;
